import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Table, Tag, Select, Input, Card, Space, Typography, DatePicker, Button, Tooltip, Alert, Spin } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, ClockCircleOutlined, UserOutlined, DatabaseOutlined, NumberOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import SideBar from '../../components/SideBar';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import axiosInstance from '../../variables/api/axiosInstance';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const colors = {
    primary: '#242166',
    secondary: '#3B82F6',
    accent: '#60A5FA',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
    background: '#F8FAFC',
    cardBg: '#FFFFFF',
    dark: '#1E293B',
    border: '#E2E8F0',
    text: '#334155',
    textLight: '#64748B',
};

const headerStyle = {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
    padding: '32px 40px',
    borderRadius: '16px 16px 0 0',
    marginBottom: '0',
};

const cardStyle = {
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(36, 33, 102, 0.1)',
    border: 'none',
    overflow: 'hidden',
};

const filterSectionStyle = {
    background: colors.cardBg,
    padding: '24px 40px',
    borderBottom: `1px solid ${colors.border}`,
};

const LogsViewer = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tableOptions, setTableOptions] = useState([]);
    const [filters, setFilters] = useState({
        table: undefined,
        codeId: '',
        userId: '',
        dateRange: [null, null],
    });

    const resolveUsername = (createdBy) => {
        if (!createdBy) return '';
        if (typeof createdBy === 'string') return createdBy;
        return (
            createdBy.username ||
            createdBy.userName ||
            createdBy.login ||
            createdBy.codeId ||
            createdBy.id ||
            ''
        );
    };

    const mapLogRow = (log) => ({
        ...log,
        createdBy: resolveUsername(log.createdBy),
        parentCodeId: log.parentCodeId || log.codeId || log.histCodeId || '',
    });

    const columns = [
        {
            title: 'Date / Heure',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 210,
            render: (text) => (
                <span style={{ color: colors.textLight, fontFamily: 'monospace', fontSize: '13px' }}>
                    {dayjs(text).format('YYYY-MM-DD HH:mm:ss')}
                </span>
            ),
        },
        {
            title: 'Utilisateur',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 220,
            render: (text) => (
                <span style={{ color: colors.textLight, fontSize: '13px' }}>{text || '—'}</span>
            ),
        },
        {
            title: 'Libellé',
            dataIndex: 'libelle',
            key: 'libelle',
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span style={{ color: colors.text, fontSize: '14px' }}>{text}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Table',
            dataIndex: 'fromTable',
            key: 'fromTable',
            width: 160,
            render: (table) => (
                <Tag
                    style={{
                        background: `${colors.primary}10`,
                        border: `1px solid ${colors.primary}30`,
                        color: colors.primary,
                        fontWeight: '500',
                        padding: '4px 12px',
                        borderRadius: '6px',
                    }}
                >
                    {table}
                </Tag>
            ),
        },
        {
            title: 'CodeId',
            dataIndex: 'parentCodeId',
            key: 'parentCodeId',
            width: 200,
            render: (text) => (
                <span style={{ color: colors.textLight, fontFamily: 'monospace', fontSize: '13px' }}>
                    {text || '—'}
                </span>
            ),
        },
    ];

    const buildQueryParams = () => {
        const params = new URLSearchParams();
        if (filters.table) params.append('table', filters.table);
        if (filters.codeId) params.append('codeId', filters.codeId.trim());
        if (filters.userId) params.append('userId', filters.userId.trim());
        if (filters.dateRange[0]) params.append('startDate', filters.dateRange[0].toISOString());
        if (filters.dateRange[1]) params.append('endDate', filters.dateRange[1].toISOString());
        return params.toString();
    };

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const query = buildQueryParams();
            const url = query ? `/api/logs?${query}` : '/api/logs';
            const { data } = await axiosInstance.get(url);
            const parsedLogs = Array.isArray(data) ? data : data?.content || [];
            const normalizedLogs = parsedLogs.map(mapLogRow);
            setLogs(normalizedLogs);
            const uniqueTables = Array.from(
                new Set(['HTTP_TRAFFIC', ...normalizedLogs.map((l) => l.fromTable).filter(Boolean)])
            );
            setTableOptions(uniqueTables);
        } catch (err) {
            setError('Impossible de charger les logs. Merci de réessayer.');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const stats = useMemo(() => {
        const total = logs.length;
        const byTable = logs.reduce((acc, log) => {
            if (log.fromTable) {
                acc[log.fromTable] = (acc[log.fromTable] || 0) + 1;
            }
            return acc;
        }, {});
        return { total, byTable };
    }, [logs]);

    const resetFilters = () => {
        setFilters({
            table: undefined,
            codeId: '',
            userId: '',
            dateRange: [null, null],
        });
    };

    return (
        <div className="layout-wrapper layout-content-navbar  ">
            <div className="layout-container">
                <SideBar />

                {/* <!-- Layout container --> */}
                <div className="layout-page">
                    <NavBar />

                    {/* <!-- Content wrapper --> */}
                    <div className="content-wrapper">
                        {/* <!-- Content --> */}
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ background: colors.background, minHeight: '100vh', padding: '40px' }}>
                                <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
                                    {/* Header */}
                                    <div style={headerStyle}>
                                        <Title level={2} style={{ color: '#FFFFFF', margin: 0, marginBottom: '8px' }}>
                                            📊 Logs d'audit
                                        </Title>
                                        <p style={{ color: '#E0E7FF', margin: 0, fontSize: '15px' }}>
                                            Visualisez et filtrez les événements d'audit (triés du plus récent au plus ancien)
                                        </p>
                                    </div>

                                    {/* Statistiques rapides */}
                                    <div style={{ padding: '24px 40px', background: colors.cardBg }}>
                                        <Space size="large" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <Tag icon={<ClockCircleOutlined />} color={colors.primary} style={{ padding: '6px 10px', fontWeight: 600 }}>
                                                    {stats.total} log(s)
                                                </Tag>
                                                <Tag icon={<DatabaseOutlined />} color="blue" style={{ padding: '6px 10px', fontWeight: 600 }}>
                                                    {Object.keys(stats.byTable).length} tables
                                                </Tag>
                                            </div>
                                            <div style={{ color: colors.textLight, fontSize: 13 }}>
                                                L'API renvoie déjà les logs triés du plus récent au plus ancien.
                                            </div>
                                        </Space>
                                    </div>

                                    {/* Filtres */}
                                    <div style={filterSectionStyle}>
                                        <Space size="middle" wrap style={{ width: '100%' }}>
                                            <Select
                                                placeholder="Table (TypeTable)"
                                                style={{ width: 220 }}
                                                allowClear
                                                onChange={(value) => setFilters((prev) => ({ ...prev, table: value }))}
                                                value={filters.table}
                                                size="large"
                                            >
                                                {tableOptions.map((table) => (
                                                    <Option key={table} value={table}>{table}</Option>
                                                ))}
                                            </Select>
                                            <Input
                                                placeholder="CodeId (parentCodeId)"
                                                prefix={<NumberOutlined style={{ color: colors.textLight }} />}
                                                value={filters.codeId}
                                                onChange={(e) => setFilters((prev) => ({ ...prev, codeId: e.target.value }))}
                                                style={{ width: 240 }}
                                                size="large"
                                                allowClear
                                            />
                                            <Input
                                                placeholder="Utilisateur (username)"
                                                prefix={<UserOutlined style={{ color: colors.textLight }} />}
                                                value={filters.userId}
                                                onChange={(e) => setFilters((prev) => ({ ...prev, userId: e.target.value }))}
                                                style={{ width: 240 }}
                                                size="large"
                                                allowClear
                                            />
                                            <RangePicker
                                                showTime
                                                format="YYYY-MM-DD HH:mm:ss"
                                                value={filters.dateRange}
                                                onChange={(dates) => setFilters((prev) => ({ ...prev, dateRange: dates }))}
                                                style={{ borderRadius: '8px' }}
                                                size="large"
                                            />
                                            <Space style={{ marginLeft: 'auto' }}>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    size="large"
                                                    style={{
                                                        borderRadius: '8px',
                                                        border: `1px solid ${colors.border}`,
                                                    }}
                                                    onClick={() => {
                                                        resetFilters();
                                                        setTimeout(fetchLogs, 0);
                                                    }}
                                                >
                                                    Réinitialiser
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    icon={<SearchOutlined />}
                                                    size="large"
                                                    style={{
                                                        background: colors.primary,
                                                        borderColor: colors.primary,
                                                        borderRadius: '8px',
                                                    }}
                                                    onClick={fetchLogs}
                                                    loading={loading}
                                                >
                                                    Filtrer
                                                </Button>
                                                <Button
                                                    type="default"
                                                    icon={<DownloadOutlined />}
                                                    size="large"
                                                    style={{
                                                        borderRadius: '8px',
                                                        border: `1px solid ${colors.border}`,
                                                    }}
                                                    disabled
                                                >
                                                    Exporter (bientôt)
                                                </Button>
                                            </Space>
                                        </Space>
                                    </div>

                                    {/* Table */}
                                    <div style={{ padding: '0 40px 40px 40px' }}>
                                        {error && (
                                            <Alert
                                                type="error"
                                                message={error}
                                                showIcon
                                                style={{ marginBottom: 16 }}
                                            />
                                        )}
                                        <Spin spinning={loading}>
                                            <Table
                                                columns={columns}
                                                dataSource={logs}
                                                rowKey={(record) => record.historiqueId || record.histCodeId}
                                                pagination={{
                                                    pageSize: 10,
                                                    showSizeChanger: true,
                                                    showTotal: (total) => `Total: ${total} logs`,
                                                    style: { marginTop: '24px' },
                                                }}
                                                style={{
                                                    background: colors.cardBg,
                                                }}
                                                locale={{
                                                    emptyText: loading ? 'Chargement...' : 'Aucun log trouvé pour ces filtres',
                                                }}
                                                onRow={(record) => ({
                                                    style: {
                                                        cursor: 'default',
                                                        transition: 'all 0.2s ease',
                                                    },
                                                    onMouseEnter: (e) => {
                                                        e.currentTarget.style.background = `${colors.primary}05`;
                                                    },
                                                    onMouseLeave: (e) => {
                                                        e.currentTarget.style.background = 'transparent';
                                                    },
                                                })}
                                            />
                                        </Spin>
                                    </div>
                                </Card>
                            </div>
                        </div>
                        {/* <!-- / Content --> */}

                        {/* <!-- Footer --> */}
                        <Footer />
                        {/* <!-- / Footer --> */}

                        <div className="content-backdrop fade"></div>
                    </div>
                    {/* <!-- Content wrapper --> */}
                </div>
                {/* <!-- / Layout page --> */}
            </div>

            {/* <!-- Overlay --> */}
            <div className="layout-overlay layout-menu-toggle"></div>

            {/* <!-- Drag Target Area To SlideIn Menu On Small Screens --> */}
            <div className="drag-target"></div>
        </div>
    );
};

export default LogsViewer;
